/**
 * Logic thuần cho việc ghi document `schedules` khi đăng ký / đổi ca / tách ca.
 *
 * Quy ước mã document: `{date}_{userId}_{shiftType}`. Tuy nhiên sau khi ĐỔI CA, document
 * được giữ nguyên mã cũ (để log "Đổi Ca", yêu cầu về sớm, làm thêm giờ... vẫn trỏ đúng),
 * nên mã KHÔNG còn chắc chắn khớp `shiftType`. Mọi nơi tạo ca mới phải chọn mã bằng
 * `pickScheduleDocId` thay vì tự ghép chuỗi, nếu không sẽ ghi đè nhầm ca khác.
 *
 * ⚠️ Giữ ĐỒNG BỘ `pickScheduleDocId` với bản CommonJS trong `functions/overtimeShared.js`.
 */

export const buildScheduleDocId = (date, userId, shiftType) => `${date}_${userId}_${shiftType}`;

/**
 * Chọn mã document cho một ca của nhân viên trong ngày mà không đè lên ca khác.
 *  1. Nhân viên đã có document cùng loại ca trong ngày → dùng lại document đó
 *     (ưu tiên ca chưa hủy; ca đã hủy sẽ được ghi đè để "đăng ký lại").
 *  2. Mã chuẩn còn trống → dùng mã chuẩn.
 *  3. Mã chuẩn đang chứa một loại ca khác (do đổi ca) → thêm hậu tố `_2`, `_3`...
 *
 * @param {string} date - 'YYYY-MM-DD'
 * @param {string} userId
 * @param {string} shiftType
 * @param {Array<{id: string, shiftType: string, status?: string, userId?: string, date?: string}>} daySchedules
 *        Các lịch (kèm id) trong ngày; lịch của người khác / ngày khác bị bỏ qua.
 * @returns {string}
 */
export const pickScheduleDocId = (date, userId, shiftType, daySchedules = []) => {
  const own = (daySchedules || []).filter(s =>
    s && s.id && (!s.userId || s.userId === userId) && (!s.date || s.date === date)
  );

  const sameShift = own.filter(s => s.shiftType === shiftType);
  if (sameShift.length > 0) {
    return (sameShift.find(s => s.status !== 'cancelled') || sameShift[0]).id;
  }

  const baseId = buildScheduleDocId(date, userId, shiftType);
  const taken = new Set(own.map(s => s.id));
  if (!taken.has(baseId)) return baseId;

  let n = 2;
  while (taken.has(`${baseId}_${n}`)) n += 1;
  return `${baseId}_${n}`;
};

const HALF_DAY_SHIFTS = ['morning', 'afternoon'];
const LEAVE_STATUSES = ['on_leave_pending', 'on_leave_approved', 'emergency_leave'];

export const SHIFT_CONFLICT = {
  HAS_FULL_DAY: 'has_full_day',         // đã có ca Cả ngày, không đăng ký thêm Sáng/Chiều
  DUPLICATE_SHIFT: 'duplicate_shift',   // đã có đúng loại ca này trong ngày
  OVERLAP_ON_LEAVE: 'overlap_on_leave', // gộp Cả ngày sẽ xóa một ca đang xin nghỉ / đã duyệt nghỉ
  OVERLAP_OVERTIME: 'overlap_overtime', // gộp Cả ngày sẽ xóa một ca làm thêm giờ đã duyệt
};

/**
 * Kiểm tra việc đăng ký / đổi sang `shiftsToRegister` có xung đột với các ca KHÁC của
 * nhân viên trong cùng ngày hay không.
 *
 * @param {Object} params
 * @param {Array} params.daySchedules - các ca khác của nhân viên trong ngày (đã loại ca đang sửa)
 * @param {string[]} params.shiftsToRegister - loại ca sắp ghi
 * @param {boolean} [params.checkDuplicate=true] - chặn khi đã có đúng loại ca đó (đăng ký mới / đổi ca)
 * @returns {{ conflict: null | { code: string, shiftType?: string }, deleteIds: string[] }}
 *          deleteIds: các ca Sáng/Chiều bị ca Cả ngày bao trùm, cần xóa CÙNG batch với thao tác ghi.
 */
export const checkShiftConflicts = ({ daySchedules = [], shiftsToRegister = [], checkDuplicate = true }) => {
  const active = (daySchedules || []).filter(s => s && s.status !== 'cancelled');

  if (active.some(s => s.shiftType === 'full_day') && shiftsToRegister.some(st => HALF_DAY_SHIFTS.includes(st))) {
    return { conflict: { code: SHIFT_CONFLICT.HAS_FULL_DAY }, deleteIds: [] };
  }

  if (checkDuplicate) {
    const duplicated = shiftsToRegister.find(st => active.some(s => s.shiftType === st));
    if (duplicated) {
      return { conflict: { code: SHIFT_CONFLICT.DUPLICATE_SHIFT, shiftType: duplicated }, deleteIds: [] };
    }
  }

  if (shiftsToRegister.includes('full_day')) {
    const covered = active.filter(s => HALF_DAY_SHIFTS.includes(s.shiftType));
    const onLeave = covered.find(s => LEAVE_STATUSES.includes(s.status));
    if (onLeave) {
      return { conflict: { code: SHIFT_CONFLICT.OVERLAP_ON_LEAVE, shiftType: onLeave.shiftType }, deleteIds: [] };
    }
    const overtime = covered.find(s => s.isOvertime);
    if (overtime) {
      return { conflict: { code: SHIFT_CONFLICT.OVERLAP_OVERTIME, shiftType: overtime.shiftType }, deleteIds: [] };
    }
    return { conflict: null, deleteIds: covered.map(s => s.id) };
  }

  return { conflict: null, deleteIds: [] };
};

const SHIFT_NAMES = {
  morning: 'Ca Sáng',
  lunch: 'Ca Trưa',
  afternoon: 'Ca Chiều',
  evening: 'Ca Tối',
  full_day: 'Cả ngày',
};

export const getShiftName = (shiftType) => SHIFT_NAMES[shiftType] || shiftType || '';

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' */
export const formatScheduleDate = (dateStr) =>
  (typeof dateStr === 'string' && dateStr ? dateStr.split('-').reverse().join('/') : '');

/** Thông báo lỗi hiển thị cho người dùng ứng với kết quả `checkShiftConflicts`. */
export const getShiftConflictMessage = (conflict, dateStr) => {
  if (!conflict) return '';
  const day = formatScheduleDate(dateStr);
  const shift = getShiftName(conflict.shiftType);
  switch (conflict.code) {
    case SHIFT_CONFLICT.HAS_FULL_DAY:
      return `Ngày ${day} đã có ca "Cả ngày". Cả ngày đã bao gồm ca Sáng và Chiều, không thể đăng ký trùng lặp.`;
    case SHIFT_CONFLICT.DUPLICATE_SHIFT:
      return `Ngày ${day} đã có "${shift}" rồi, không thể đăng ký hoặc đổi sang trùng ca.`;
    case SHIFT_CONFLICT.OVERLAP_ON_LEAVE:
      return `Ngày ${day} có "${shift}" đang xin nghỉ hoặc đã được duyệt nghỉ. Hãy xử lý ca đó trước khi chuyển thành "Cả ngày".`;
    case SHIFT_CONFLICT.OVERLAP_OVERTIME:
      return `Ngày ${day} có "${shift}" là ca làm thêm giờ đã duyệt. Hãy thu hồi ca làm thêm trước khi chuyển thành "Cả ngày".`;
    default:
      return `Ngày ${day} có ca làm việc bị trùng, không thể lưu.`;
  }
};
